/**
 * Role Levels - Hierarchical access levels for the PMS system
 */
export const RoleLevels = {
    SUPER_ADMIN: 1,        // System-wide access
    INTER_PHARMACY: 10,    // Multi-pharmacy management
    PHARMACY: 20,          // Pharmacy level management
    BRANCH: 30,            // Branch level operations
} as const;

/**
 * User Types
 */
export const UserTypes = {
    ADMIN: 'admin',
    MANAGER: 'manager',
    CASHIER: 'cashier',
} as const;

/**
 * Helper functions for role checking
 */
export const RoleUtils = {
    /**
     * Get role name by level ID
     */
    getRoleName(levelId: number): string | null {
        const roles = Object.entries(RoleLevels).find(([_, value]) => value === levelId);
        return roles ? roles[0] : null;
    },

    /**
     * Check if a level ID is valid
     */
    isValidRole(levelId: number): boolean {
        return (Object.values(RoleLevels) as number[]).includes(levelId);
    },

    /**
     * Check if user has admin-level access (Super Admin or Inter-Pharmacy)
     */
    isAdmin(levelId: number): boolean {
        return levelId === RoleLevels.SUPER_ADMIN || levelId === RoleLevels.INTER_PHARMACY;
    },

    /**
     * Check if user has pharmacy-level access
     */
    isPharmacyLevel(levelId: number): boolean {
        return levelId === RoleLevels.PHARMACY;
    },

    /**
     * Check if user has branch-level access
     */
    isBranchLevel(levelId: number): boolean {
        return levelId === RoleLevels.BRANCH;
    },

    /**
     * Get all role levels as an array
     */
    getAllLevels(): number[] {
        return Object.values(RoleLevels);
    }
};

// Type definitions
export type RoleLevel = typeof RoleLevels[keyof typeof RoleLevels];
export type UserType = typeof UserTypes[keyof typeof UserTypes];
